import { useEffect, useMemo, useState } from 'react';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import { Skeletons, RM, RMk } from '../components/kit';
import * as teamApi from '../api/team';
import * as earningsApi from '../api/earnings';

// In-year "peak" steps above the 85% lifetime cap. Values are the server truth
// (earnings/home.service: STEP_87 = 250k, STEP_90 = 500k of current-year GCI) — the
// design mock's "300k" for 87% is stale, so we use the correct 250k.
const PEAK_STEPS = [
  { tier: 90, need: 500000 },
  { tier: 87, need: 250000 },
];

// Per-state node + card styling for the climbing track (mirrors the design mock).
const NODE = {
  done: { size: 34, bg: 'var(--ok)', fg: '#fff', bd: 'var(--ok)', glow: 'none', fs: 12 },
  cur: { size: 46, bg: 'var(--primary)', fg: '#fff', bd: 'var(--primary)', glow: '0 0 0 4px color-mix(in srgb,var(--primary) 22%,transparent)', fs: 13 },
  future: { size: 34, bg: 'var(--surface)', fg: 'var(--muted)', bd: 'var(--border)', glow: 'none', fs: 12 },
};
const CARD = {
  done: { bg: 'var(--surface)', bd: 'var(--border)', title: 'var(--text)' },
  cur: { bg: 'color-mix(in srgb,var(--primary) 8%,var(--surface))', bd: 'var(--primary)', title: 'var(--text)' },
  future: { bg: 'var(--surface2)', bd: 'var(--border)', title: 'var(--muted)' },
};

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 13 10 18 19 7" /></svg>
);
const Lock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="6" y="11" width="12" height="8" rx="2" /><path d="M9 11 V8 a3 3 0 0 1 6 0 v3" /></svg>
);

const stateOf = (t) => (t.current ? 'cur' : t.unlocked ? 'done' : 'future');

export default function TierLadderPage() {
  const { message } = AntApp.useApp();
  const [rungs, setRungs] = useState(null);
  const [sum, setSum] = useState(null);

  useEffect(() => {
    Promise.all([
      teamApi.listCareerTierLadder({ limit: 50 }).then((r) => r?.data ?? r),
      earningsApi.listEarningsSummary().then((r) => r?.data ?? r),
    ])
      .then(([ladder, summary]) => { setRungs(ladder || []); setSum(summary || {}); })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = !rungs || !sum;

  // Highest tier at the top so the track reads as a climb toward the summit.
  const ordered = useMemo(() => [...(rungs || [])].sort((a, b) => b.tier - a.tier), [rungs]);

  const hero = useMemo(() => {
    if (loading) return null;
    const cur = (rungs || []).find((r) => r.current);
    const curThreshold = cur?.threshold ?? 0;
    const nextRung = sum?.nextTier != null ? (rungs || []).find((r) => r.tier === sum.nextTier) : null;
    const nextThreshold = nextRung?.threshold;
    const lifetime = sum?.lifetimeGci ?? 0;
    let pct = 100;
    if (nextThreshold != null && nextThreshold > curThreshold) {
      pct = Math.max(0, Math.min(100, Math.round(((lifetime - curThreshold) / (nextThreshold - curThreshold)) * 100)));
    }
    return {
      tier: `${sum?.tier ?? cur?.tier ?? '—'}%`,
      lifetimeF: RM(lifetime),
      pct,
      nextTier: sum?.nextTier ?? null,
      toNextF: RM(sum?.toNextTier ?? 0),
    };
  }, [loading, rungs, sum]);

  // Spine gradient: locked (border) above the current rung, primary at it, achieved (ok) below.
  const spine = useMemo(() => {
    const n = ordered.length;
    const curIdx = ordered.findIndex((r) => r.current);
    if (n === 0 || curIdx < 0) return 'var(--border)';
    const lockEnd = Math.round((curIdx / n) * 100);
    const curEnd = Math.round(((curIdx + 1) / n) * 100);
    return `linear-gradient(to bottom, var(--border) 0 ${lockEnd}%, var(--primary) ${lockEnd}% ${curEnd}%, var(--ok) ${curEnd}% 100%)`;
  }, [ordered]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="GCI tier ladder" sub="lifetime accumulated GCI" />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loading ? (
          <Skeletons n={5} height={72} />
        ) : (
          <>
            {/* Hero — current tier + progress to next */}
            <div style={{ background: 'linear-gradient(160deg,#241854,#18345E)', borderRadius: 16, padding: 18, color: '#fff', display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, letterSpacing: '.06em' }}>CURRENT TIER</div>
                  <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>{hero.tier}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11.5, opacity: 0.72, fontWeight: 600, letterSpacing: '.06em' }}>LIFETIME GCI</div>
                  <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{hero.lifetimeF}</div>
                </div>
              </div>
              <div style={{ height: 7, borderRadius: 999, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
                <div style={{ width: `${hero.pct}%`, height: '100%', background: '#5AA1EA', borderRadius: 999, animation: 'fillup .9s ease' }} />
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.85 }}>
                {hero.nextTier != null
                  ? <>{hero.toNextF} to reach <b>{hero.nextTier}%</b></>
                  : <>Top lifetime tier reached — chase the peak below.</>}
              </div>
            </div>

            {/* Peak zone — in-year steps above 85% */}
            <div style={{ border: '1.5px solid color-mix(in srgb,var(--warn) 45%,var(--border))', background: 'color-mix(in srgb,var(--warn) 7%,var(--surface))', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--warn)' }}>▲ PEAK · THIS YEAR</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>resets 1 Jan</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {PEAK_STEPS.map((s) => (
                  <div key={s.tier} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid color-mix(in srgb,var(--warn) 30%,var(--border))', borderRadius: 12, padding: '14px 15px' }}>
                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'color-mix(in srgb,var(--warn) 16%,transparent)', color: 'var(--warn)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flex: 'none' }}>{s.tier}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{s.tier}%</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{RMk(s.need)} in-year</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>A seasonal challenge above 85% — unlocked from current-year GCI, separate from the lifetime ladder.</div>
            </div>

            {/* Climbing track — lifetime rungs 60 → 85 */}
            <div style={{ position: 'relative', paddingLeft: 6 }}>
              <div style={{ position: 'absolute', left: 32, top: 14, bottom: 14, width: 2.5, background: spine, borderRadius: 999 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {ordered.map((t) => {
                  const st = stateOf(t);
                  const n = NODE[st];
                  const c = CARD[st];
                  return (
                    <div key={t.tier} style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                      <div style={{ width: 54, display: 'flex', justifyContent: 'center', flex: 'none' }}>
                        <div style={{ width: n.size, height: n.size, borderRadius: '50%', background: n.bg, color: n.fg, border: `2px solid ${n.bd}`, boxShadow: n.glow, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: n.fs, fontWeight: 700, position: 'relative', zIndex: 1 }}>
                          {st === 'done' ? <Check /> : st === 'cur' ? t.tier : <Lock />}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: c.title }}>{t.tier}%</div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{RMk(t.threshold)} lifetime</div>
                        </div>
                        {st === 'cur' && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'var(--primary)', color: '#fff', letterSpacing: '.05em', flex: 'none', whiteSpace: 'nowrap' }}>YOU ARE HERE</span>}
                        {st === 'done' && <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'color-mix(in srgb,var(--ok) 15%,transparent)', color: 'var(--ok)', flex: 'none' }}>ACHIEVED</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ height: 6 }} />
          </>
        )}
      </div>
    </div>
  );
}
